import { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  MapPin, Search, CheckCircle, AlertTriangle, Download, 
  FileText, Euro, TrendingUp, Building2, RefreshCw,
  FileSpreadsheet, Upload, Settings, Link, ExternalLink,
  Loader2, Map, Calculator, PieChart, BarChart3, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Formats d'export disponibles
const FORMATS_EXPORT = [
  { code: 'fec', nom: 'FEC', description: 'Format légal français', icon: FileText },
  { code: 'csv', nom: 'CSV', description: 'Export tableur', icon: FileSpreadsheet },
  { code: 'cegid', nom: 'Cegid', description: 'Cegid Comptabilité', icon: Building2 },
  { code: 'sage', nom: 'Sage', description: 'Sage 50/100', icon: Calculator },
  { code: 'quickbooks', nom: 'QuickBooks', description: 'Format IIF', icon: TrendingUp },
  { code: 'ebp', nom: 'EBP', description: 'EBP Comptabilité', icon: FileText }
];

// Intégrations disponibles
const INTEGRATIONS = [
  { code: 'pennylane', nom: 'Pennylane', statut: 'disponible', logo: '📊' },
  { code: 'indy', nom: 'Indy', statut: 'disponible', logo: '📈' },
  { code: 'tiime', nom: 'Tiime', statut: 'bientot', logo: '⏱️' },
  { code: 'expert_comptable', nom: 'Expert-Comptable', statut: 'disponible', logo: '👨‍💼' }
];

export default function ComptaZRRExportPage() {
  const { activeSociete } = useWorkspace();
  const [activeTab, setActiveTab] = useState('zrr');
  const [loading, setLoading] = useState(false);
  
  // États ZRR
  const [rapportZRR, setRapportZRR] = useState(null);
  const [clientsNonClassifies, setClientsNonClassifies] = useState([]);
  const [verificationCommune, setVerificationCommune] = useState(null);
  const [communeSearch, setCommuneSearch] = useState({ code_postal: '', nom: '' });
  
  // États Export
  const [dateDebut, setDateDebut] = useState(new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0]);
  const [dateFin, setDateFin] = useState(new Date().toISOString().split('T')[0]);
  const [formatExport, setFormatExport] = useState('fec');
  const [apercuExport, setApercuExport] = useState(null);
  
  // États Classification auto
  const [classificationEnCours, setClassificationEnCours] = useState(false);
  const [progressClassification, setProgressClassification] = useState(0);

  const fetchRapportZRR = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'X-Societe-Id': activeSociete?.id || ''
    };

    try {
      const res = await fetch(
        `${API_URL}/api/comptabilite/zrr/rapport?date_debut=${dateDebut}&date_fin=${dateFin}`,
        { headers }
      );
      if (res.ok) {
        const data = await res.json();
        setRapportZRR(data);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  }, [activeSociete, dateDebut, dateFin]);

  const fetchClientsNonClassifies = useCallback(async () => {
    const token = localStorage.getItem('token');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'X-Societe-Id': activeSociete?.id || ''
    };

    try {
      const res = await fetch(`${API_URL}/api/comptabilite/zrr/clients-a-classifier`, { headers });
      if (res.ok) {
        const data = await res.json();
        setClientsNonClassifies(data.clients_non_classifies || []);
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  }, [activeSociete]);

  useEffect(() => {
    if (activeTab === 'zrr') {
      fetchRapportZRR();
      fetchClientsNonClassifies();
    }
  }, [activeTab, fetchRapportZRR, fetchClientsNonClassifies]);

  const verifierCommune = async () => {
    if (!communeSearch.code_postal && !communeSearch.nom) {
      toast.error('Entrez un code postal ou un nom de commune');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      const params = new URLSearchParams();
      if (communeSearch.code_postal) params.append('code_postal', communeSearch.code_postal);
      if (communeSearch.nom) params.append('nom_commune', communeSearch.nom);

      const res = await fetch(
        `${API_URL}/api/comptabilite/zrr/verifier-commune?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        setVerificationCommune(data);
      }
    } catch (error) {
      toast.error('Erreur lors de la vérification');
    } finally {
      setLoading(false);
    }
  };

  const lancerClassificationAuto = async () => {
    setClassificationEnCours(true);
    setProgressClassification(0);
    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${API_URL}/api/comptabilite/zrr/auto-classifier-clients`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Societe-Id': activeSociete?.id || ''
        }
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.clients_classifies} clients classifiés !`);
        setProgressClassification(100);
        fetchClientsNonClassifies();
        fetchRapportZRR();
      }
    } catch (error) {
      toast.error('Erreur lors de la classification');
    } finally {
      setClassificationEnCours(false);
    }
  };

  const fetchApercuExport = async () => {
    const token = localStorage.getItem('token');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'X-Societe-Id': activeSociete?.id || ''
    };

    try {
      const res = await fetch(
        `${API_URL}/api/comptabilite/export/apercu?date_debut=${dateDebut}&date_fin=${dateFin}`,
        { headers }
      );
      if (res.ok) {
        const data = await res.json();
        setApercuExport(data);
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const telechargerExport = async () => {
    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${API_URL}/api/comptabilite/export/generer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Societe-Id': activeSociete?.id || ''
        },
        body: JSON.stringify({
          date_debut: dateDebut,
          date_fin: dateFin,
          format: formatExport,
          type_export: 'complet',
          include_frr: true
        })
      });

      if (res.ok) {
        const contentType = res.headers.get('content-type');
        
        if (contentType && (contentType.includes('text/plain') || contentType.includes('text/csv'))) {
          const blob = await res.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `export_compta_${formatExport}_${dateDebut}_${dateFin}.${formatExport === 'csv' ? 'csv' : 'txt'}`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          a.remove();
          toast.success('Export téléchargé !');
        } else {
          const data = await res.json();
          toast.success(data.message || 'Export généré');
        }
      }
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6" data-testid="compta-zrr-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Comptabilité Avancée</h1>
            <p className="text-slate-600">Zone FRR (ex-ZRR), Exports & Intégrations</p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white/80">
          <TabsTrigger value="zrr">Zone FRR/ZRR</TabsTrigger>
          <TabsTrigger value="export">Export Comptable</TabsTrigger>
          <TabsTrigger value="integrations">Intégrations</TabsTrigger>
        </TabsList>

        {/* TAB ZRR/FRR */}
        <TabsContent value="zrr">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Colonne gauche - Stats et Rapport */}
            <div className="lg:col-span-2 space-y-4">
              {/* Période */}
              <Card className="bg-white/80 backdrop-blur">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Rapport CA - Zone FRR</CardTitle>
                  <CardDescription>
                    Chiffre d'affaires différencié pour vos déclarations fiscales
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end gap-4 mb-4">
                    <div>
                      <Label>Date début</Label>
                      <Input
                        type="date"
                        value={dateDebut}
                        onChange={(e) => setDateDebut(e.target.value)}
                        className="w-40"
                      />
                    </div>
                    <div>
                      <Label>Date fin</Label>
                      <Input
                        type="date"
                        value={dateFin}
                        onChange={(e) => setDateFin(e.target.value)}
                        className="w-40"
                      />
                    </div>
                    <Button onClick={fetchRapportZRR} disabled={loading}>
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      <span className="ml-2">Actualiser</span>
                    </Button>
                  </div>

                  {rapportZRR && (
                    <div className="space-y-4">
                      {/* Stats principales */}
                      <div className="grid grid-cols-3 gap-4">
                        <div className="p-4 bg-slate-100 rounded-lg text-center">
                          <p className="text-sm text-gray-500">CA Total HT</p>
                          <p className="text-2xl font-bold text-slate-900">
                            {rapportZRR.synthese?.ca_total_ht?.toLocaleString('fr-FR')} €
                          </p>
                        </div>
                        <div className="p-4 bg-green-100 rounded-lg text-center">
                          <p className="text-sm text-green-700">CA Zone FRR</p>
                          <p className="text-2xl font-bold text-green-800">
                            {rapportZRR.synthese?.ca_frr_ht?.toLocaleString('fr-FR')} €
                          </p>
                          <Badge className="bg-green-500 mt-1">
                            {rapportZRR.synthese?.pourcentage_frr}%
                          </Badge>
                        </div>
                        <div className="p-4 bg-gray-100 rounded-lg text-center">
                          <p className="text-sm text-gray-500">CA Hors FRR</p>
                          <p className="text-2xl font-bold text-gray-700">
                            {rapportZRR.synthese?.ca_hors_frr_ht?.toLocaleString('fr-FR')} €
                          </p>
                        </div>
                      </div>

                      {/* Avantages fiscaux */}
                      {rapportZRR.avantages_fiscaux?.exoneration_impot_societes && (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                            <div>
                              <p className="font-medium text-blue-900">Avantages fiscaux FRR</p>
                              <p className="text-sm text-blue-700 mt-1">
                                {rapportZRR.avantages_fiscaux.note}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Nb factures */}
                      <div className="flex gap-4 text-sm text-gray-600">
                        <span>Total factures: {rapportZRR.synthese?.nb_factures_total}</span>
                        <span>|</span>
                        <span className="text-green-600">FRR: {rapportZRR.synthese?.nb_factures_frr}</span>
                        <span>|</span>
                        <span>Hors FRR: {rapportZRR.synthese?.nb_factures_hors_frr}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Classification clients */}
              <Card className="bg-white/80 backdrop-blur">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">Classification clients FRR</CardTitle>
                      <CardDescription>
                        {clientsNonClassifies.length} clients à classifier
                      </CardDescription>
                    </div>
                    <Button 
                      onClick={lancerClassificationAuto}
                      disabled={classificationEnCours || clientsNonClassifies.length === 0}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {classificationEnCours ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Classification auto
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {classificationEnCours && (
                    <div className="mb-4">
                      <Progress value={progressClassification} className="h-2" />
                      <p className="text-sm text-gray-500 mt-1">Classification en cours...</p>
                    </div>
                  )}
                  
                  {clientsNonClassifies.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {clientsNonClassifies.slice(0, 10).map((client, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div>
                            <span className="font-medium">{client.nom}</span>
                            <span className="text-sm text-gray-500 ml-2">
                              {client.code_postal} {client.ville}
                            </span>
                          </div>
                          <Badge variant="outline">À classifier</Badge>
                        </div>
                      ))}
                      {clientsNonClassifies.length > 10 && (
                        <p className="text-sm text-gray-500 text-center">
                          et {clientsNonClassifies.length - 10} autres...
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-green-600">
                      <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                      <p>Tous les clients sont classifiés !</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Colonne droite - Vérification commune */}
            <div className="space-y-4">
              <Card className="bg-white/80 backdrop-blur">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    Vérifier une commune
                  </CardTitle>
                  <CardDescription>
                    Vérifiez si une commune est en zone FRR
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Code postal</Label>
                    <Input
                      value={communeSearch.code_postal}
                      onChange={(e) => setCommuneSearch({ ...communeSearch, code_postal: e.target.value })}
                      placeholder="Ex: 26000"
                    />
                  </div>
                  <div>
                    <Label>ou Nom de commune</Label>
                    <Input
                      value={communeSearch.nom}
                      onChange={(e) => setCommuneSearch({ ...communeSearch, nom: e.target.value })}
                      placeholder="Ex: Valence"
                    />
                  </div>
                  <Button onClick={verifierCommune} className="w-full" disabled={loading}>
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                    Vérifier
                  </Button>

                  {verificationCommune && (
                    <div className={`p-4 rounded-lg ${verificationCommune.is_frr ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {verificationCommune.is_frr ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <AlertTriangle className="w-5 h-5 text-gray-500" />
                        )}
                        <span className="font-medium">
                          {verificationCommune.commune_info?.nom || 'Commune'}
                        </span>
                      </div>
                      <p className={verificationCommune.is_frr ? 'text-green-700' : 'text-gray-600'}>
                        {verificationCommune.is_frr 
                          ? `Zone FRR ${verificationCommune.frr_type || 'socle'}`
                          : 'Hors zone FRR'
                        }
                      </p>
                      {verificationCommune.raison && (
                        <p className="text-sm text-gray-500 mt-1">{verificationCommune.raison}</p>
                      )}
                      {verificationCommune.commune_info?.codeDepartement && (
                        <p className="text-xs text-gray-400 mt-2">
                          Département: {verificationCommune.commune_info.codeDepartement}
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Info FRR */}
              <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                <CardContent className="pt-6">
                  <h3 className="font-bold mb-2">France Ruralités Revitalisation</h3>
                  <p className="text-sm text-blue-100">
                    Le dispositif FRR (ex-ZRR) offre des avantages fiscaux aux entreprises 
                    situées en zones rurales fragiles : exonérations d'impôt sur les sociétés, 
                    CFE, et autres aides.
                  </p>
                  <Button variant="secondary" size="sm" className="mt-4" asChild>
                    <a href="https://www.observatoire-des-territoires.gouv.fr/frr-france-ruralites-revitalisation" target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4 mr-2" /> En savoir plus
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* TAB EXPORT */}
        <TabsContent value="export">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <Card className="bg-white/80 backdrop-blur">
                <CardHeader>
                  <CardTitle>Export Comptable</CardTitle>
                  <CardDescription>
                    Générez vos exports pour votre logiciel comptable ou expert-comptable
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Période */}
                  <div className="flex items-end gap-4">
                    <div>
                      <Label>Période du</Label>
                      <Input
                        type="date"
                        value={dateDebut}
                        onChange={(e) => setDateDebut(e.target.value)}
                        className="w-40"
                      />
                    </div>
                    <div>
                      <Label>au</Label>
                      <Input
                        type="date"
                        value={dateFin}
                        onChange={(e) => setDateFin(e.target.value)}
                        className="w-40"
                      />
                    </div>
                    <Button variant="outline" onClick={fetchApercuExport}>
                      <Eye className="w-4 h-4 mr-2" /> Aperçu
                    </Button>
                  </div>

                  {/* Aperçu */}
                  {apercuExport && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium mb-2">Aperçu de l'export</h4>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Factures:</span>
                          <span className="ml-2 font-medium">{apercuExport.statistiques?.nb_factures}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Écritures:</span>
                          <span className="ml-2 font-medium">~{apercuExport.statistiques?.nb_ecritures_estimees}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">dont FRR:</span>
                          <span className="ml-2 font-medium text-green-600">{apercuExport.statistiques?.nb_factures_frr}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Format */}
                  <div>
                    <Label className="mb-2 block">Format d'export</Label>
                    <div className="grid grid-cols-3 gap-3">
                      {FORMATS_EXPORT.map((format) => {
                        const Icon = format.icon;
                        const isSelected = formatExport === format.code;
                        return (
                          <button
                            key={format.code}
                            onClick={() => setFormatExport(format.code)}
                            className={`p-4 rounded-lg border text-left transition ${
                              isSelected 
                                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <Icon className={`w-5 h-5 mb-2 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                            <div className="font-medium">{format.nom}</div>
                            <div className="text-xs text-gray-500">{format.description}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Bouton export */}
                  <div className="flex gap-4">
                    <Button onClick={telechargerExport} className="bg-blue-600 hover:bg-blue-700">
                      <Download className="w-4 h-4 mr-2" /> Télécharger l'export {formatExport.toUpperCase()}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Info FEC */}
            <div className="space-y-4">
              <Card className="bg-white/80 backdrop-blur">
                <CardHeader>
                  <CardTitle className="text-lg">Format FEC</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-gray-600 space-y-2">
                  <p>
                    Le <strong>Fichier des Écritures Comptables (FEC)</strong> est un format 
                    légal obligatoire en France pour les contrôles fiscaux.
                  </p>
                  <p>
                    Il contient l'intégralité des écritures comptables de l'exercice 
                    dans un format standardisé.
                  </p>
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded">
                    <p className="text-amber-800 text-xs">
                      ⚠️ Conservez vos FEC pendant 6 ans minimum (obligation légale)
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* TAB INTÉGRATIONS */}
        <TabsContent value="integrations">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {INTEGRATIONS.map((integration) => (
              <Card key={integration.code} className="bg-white/80 backdrop-blur">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="text-3xl">{integration.logo}</div>
                    <div>
                      <h3 className="font-semibold">{integration.nom}</h3>
                      <Badge variant={integration.statut === 'disponible' ? 'default' : 'secondary'}>
                        {integration.statut === 'disponible' ? 'Disponible' : 'Bientôt'}
                      </Badge>
                    </div>
                  </div>
                  <Button 
                    className="w-full" 
                    variant={integration.statut === 'disponible' ? 'default' : 'outline'}
                    disabled={integration.statut !== 'disponible'}
                  >
                    {integration.statut === 'disponible' ? (
                      <>
                        <Link className="w-4 h-4 mr-2" /> Connecter
                      </>
                    ) : (
                      'Bientôt disponible'
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Info intégrations */}
          <Card className="mt-6 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <Settings className="w-12 h-12" />
                <div>
                  <h3 className="font-bold text-lg">Besoin d'une intégration spécifique ?</h3>
                  <p className="text-indigo-100">
                    Contactez-nous pour intégrer votre logiciel comptable préféré.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
